import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router';
import { CandleData } from 'client/voladeum/candles/types';
import { selectSignal } from 'ducks/signal/selectors';
import { selectStrategy } from 'ducks/strategy/selectors';
import { useVisibleTimeseries } from 'hooks/useVisibleTimeseries';
import { IndicatorContext } from 'pages/context/IndicatorContext';
import { StrategyContext, useStrategyContext } from 'pages/context/StrategyContext';
import { TriggerContext } from 'pages/context/TriggerContext';
import { useStrategyApi } from 'pages/hooks/useStrategyApi';
import { useAppSelector } from 'store/hooks';
import { parseQuery } from 'utils/queries';

import { NavigatorGraph } from 'components/chart/navigator/NavigatorGraph';
import { ManageConditionModal } from 'components/modals/conditions/ManageConditionModal';
import { ManageStrategyModal } from 'components/modals/strategies/ManageStrategyModal';
import { ManageTriggerModal } from 'components/modals/triggers/ManageTriggerModal';
import { Page } from 'components/page/Page';

import { CANDLE_WINDOW } from '../../../constants';

import { AdvancedGraph } from './components/indicators/AdvancedGraph';
import { IndicatorsGraph } from './components/indicators/IndicatorsGraph';
import { CandleSignal } from './components/indicators/types';
import { StrategyInformationSlim } from './components/information/PairInformationSlim';
import { StrategySettings } from './components/strategy-settings/StrategySettings';
import { StrategyTriggers } from './components/strategy-triggers/StrategyTriggers';

import cn from './StrategyPage.module.scss';
export const StrategyPage = () => {
  const { search } = useLocation();
  const { update } = parseQuery(search);
  const { time, open, high, low, close, volume } = useAppSelector(selectSignal);
  const voladeum_strategy = useAppSelector(selectStrategy);
  const { getStrategyPage } = useStrategyApi();
  const { setStrategy } = useStrategyContext();

  useEffect(() => {
    getStrategyPage();
  }, [getStrategyPage, update]);

  useEffect(() => {
    setStrategy(voladeum_strategy);
  }, [setStrategy, voladeum_strategy]);

  const signals = useMemo<CandleSignal>(
    () => ({
      time: time?.[0] ?? [],
      open: open?.[0] ?? [],
      high: high?.[0] ?? [],
      low: low?.[0] ?? [],
      close: close?.[0] ?? [],
      volume: volume?.[0] ?? [],
    }),
    [time, open, high, low, close, volume]
  );

  const candles = useMemo<CandleData[]>(
    () =>
      signals.time.map((t, i) => ({
        x: t ?? 0,
        o: signals.open[i] ?? 0,
        h: signals.high[i] ?? 0,
        l: signals.low[i] ?? 0,
        c: signals.close[i] ?? 0,
        v: signals.volume[i] ?? 0,
      })),
    [signals]
  );

  const { visible, setVisible } = useVisibleTimeseries(candles, CANDLE_WINDOW);

  return (
    <Page title={voladeum_strategy.strategy_name}>
      <div className={cn.section}>
        <div className={cn.indicators}>
          <StrategyInformationSlim />
          <IndicatorsGraph candles={candles} visible={visible} />
          <AdvancedGraph candles={candles} visible={visible} />
          <NavigatorGraph candles={candles} visible={visible} setVisible={setVisible} />
        </div>
        <div className={cn.strategy}>
          <StrategySettings />
          <StrategyTriggers />
        </div>
      </div>
    </Page>
  );
};

export default () => {
  return (
    <StrategyContext>
      <TriggerContext>
        <IndicatorContext>
          <StrategyPage />
          <ManageStrategyModal />
          <ManageTriggerModal />
          <ManageConditionModal />
        </IndicatorContext>
      </TriggerContext>
    </StrategyContext>
  );
};
